/**
 * Canonical frontend port of `agileco-link-classification-v3`.
 * Internal links omit target/rel; external links emit `_blank` and
 * `nofollow noopener noreferrer`. Invalid or absent tenants use exact legacy
 * mode: absolute HTTP(S) is external and every other href is internal-noop.
 *
 * v3 adds the malformed-authority family for protocol-relative (`//…`) hrefs.
 * It does NOT change v2 evaluation order, legacy mode, output attributes,
 * document-relative behavior, or the host suffix rule — it only validates the
 * literally-extracted authority under a VALID tenant, failing CLOSED (external)
 * when that authority is malformed. Malformed authorities previously fell
 * through to a substring/prefix comparison and could classify as INTERNAL,
 * stripping `nofollow` off a genuinely external host (fail-open defect).
 */
const REGISTRABLE_HOST_RE =
  /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;

const ABSOLUTE_HTTP_RE = /^https?:\/\//i;
const SCHEME_RE = /^([A-Za-z][A-Za-z0-9+.-]*):/;
/** Whitespace or a backslash anywhere in an authority is invalid. Browsers
 *  normalize `\` to `/`, so `//client.com\evil.com/x` is a real spoof channel. */
const INVALID_AUTHORITY_CHAR_RE = /[\s\\]/;
/** A port is valid only if it is strictly numeric AND inside the URL port range. */
const NUMERIC_PORT_RE = /^\d+$/;
const MAX_PORT = 65535;

export function isInternalHref(href: unknown, siteHost?: string | null): boolean {
  // Rule 1 — non-string / empty / whitespace-only → internal no-op.
  if (typeof href !== 'string') {
    return true;
  }
  const trimmed = href.trim();
  if (trimmed === '') {
    return true;
  }

  const tenantHost = normalizeTenantHost(siteHost);

  // Invalid/absent tenants use exact legacy mode before URL-shape handling.
  if (!tenantHost) {
    return !ABSOLUTE_HTTP_RE.test(trimmed);
  }

  // Network-path URLs must still be classified before the `/` shortcut when a
  // valid tenant host is available. A malformed authority fails CLOSED here.
  if (trimmed.startsWith('//')) {
    const host = protocolRelativeHost(trimmed);
    if (!host) {
      return false;
    }
    return hostRuleInternal(host, tenantHost);
  }

  // Rule 3 — `/`, `#`, `?` prefixes → internal (same-origin references).
  if (trimmed.startsWith('/') || trimmed.startsWith('#') || trimmed.startsWith('?')) {
    return true;
  }

  // Rule 4 — explicit scheme?
  const schemeMatch = SCHEME_RE.exec(trimmed);
  if (schemeMatch) {
    const scheme = schemeMatch[1].toLowerCase();
    if (scheme === 'http' || scheme === 'https') {
      let hrefHost: string;
      try {
        hrefHost = new URL(trimmed).hostname;
      } catch {
        return false;
      }
      return hostRuleInternal(hrefHost, tenantHost);
    }
    // Any other scheme (mailto:, tel:, javascript:, data:, ftp:, …) → external.
    return false;
  }

  // Rule 5 — document-relative (`services/x`, `../contact`, `./about`,
  // `page.html`) → internal.
  return true;
}

/**
 * HOST RULE — both hosts normalized, then registrable equality or subdomain
 * suffix. Never a substring match, so an "evil-suffix" host such as
 * `https://client.com.evil.test/` classifies as external.
 */
function hostRuleInternal(hrefHost: string, tenantHost: string): boolean {
  const h = normalizeHost(hrefHost);
  if (!h) {
    return false;
  }
  return h === tenantHost || h.endsWith('.' + tenantHost);
}

/**
 * v3 protocol-relative authority handling (`//authority/path`).
 *
 * Returns the validated, normalized host, or `undefined` when the authority is
 * malformed — callers treat `undefined` as fail-closed EXTERNAL under a valid
 * tenant. NEVER hand a `//…` href to `new URL()` here: a URL parser silently
 * REPAIRS an empty authority (`///client.com/path` → host `client.com`), which
 * is exactly the spoof this validator exists to reject.
 *
 * Order (spec rules 1-4):
 *  1. Extract the authority LITERALLY: everything after the leading `//`, up to
 *     the first `/`, `?`, or `#`.
 *  2. Reject empty authority, whitespace, or backslash outright.
 *  3. Parse/drop userinfo, then validate the RESULTING host — identical to the
 *     v2 full-URL semantics, where `https://user:pass@client.com/x` is INTERNAL
 *     (the real host is the tenant) while `https://client.com@evil.example/` is
 *     EXTERNAL (the real host is the attacker). `//u@h/x` resolves to the same
 *     host as `https://u@h/x`, so both surfaces classify by resolved host.
 *  4. Reject bracketed/IP-literal hosts, an empty/nonnumeric/out-of-range port,
 *     empty or invalid DNS labels, and anything failing the registrable shape.
 *     A bare-IP or bracketed host can never be INTERNAL: it could only match a
 *     tenant that is itself an IP, and tenant validation already rejects those.
 */
function protocolRelativeHost(value: string): string | undefined {
  // Rule 1 — literal extraction (no URL parser, no repair).
  const remainder = value.slice(2);
  const delimiterIndex = remainder.search(/[/?#]/);
  const authority = delimiterIndex === -1 ? remainder : remainder.slice(0, delimiterIndex);

  // Rule 2 — empty authority (`///x`, `//?x`, `//#x`), whitespace, backslash.
  if (authority === '' || INVALID_AUTHORITY_CHAR_RE.test(authority)) {
    return undefined;
  }

  // Rule 3 — drop userinfo, keep the resulting host:port (v2 userinfo semantics).
  const at = authority.lastIndexOf('@');
  const hostPort = at === -1 ? authority : authority.slice(at + 1);
  if (hostPort === '') {
    return undefined;
  }

  // Rule 4a — bracketed / IPv6-literal authority (`//[::1]/x`) is never internal.
  if (hostPort.includes('[') || hostPort.includes(']')) {
    return undefined;
  }

  // Rule 4b — at most one explicit port, strictly numeric, within `0..65535`.
  let hostPart = hostPort;
  const colon = hostPort.indexOf(':');
  if (colon !== -1) {
    hostPart = hostPort.slice(0, colon);
    const port = hostPort.slice(colon + 1);
    // Empty (`host:`), nonnumeric (`host:bad`), multi-colon (`host:1:2`), and
    // out-of-range (`host:65536`) ports are all invalid → fail closed.
    if (!NUMERIC_PORT_RE.test(port) || Number(port) > MAX_PORT) {
      return undefined;
    }
  }

  // Rule 4c — v2 host normalization, then the v2 registrable-domain shape.
  // Empty DNS labels (`evil..client.com`, `.client.com`) and invalid label
  // characters cannot satisfy REGISTRABLE_HOST_RE, so they fail closed here.
  const normalized = normalizeHost(hostPart);
  if (!REGISTRABLE_HOST_RE.test(normalized) || isIpAddress(normalized)) {
    return undefined;
  }
  return normalized;
}

/**
 * Validate + normalize the tenant host. Strips AT MOST ONE terminal dot and then
 * validates the result, so `client.com.` is valid (→ `client.com`) while
 * `client.com..` is still malformed after that single strip → INVALID → the
 * caller drops into byte-faithful legacy mode. Do not "fix" this into a
 * strip-all-dots (`rstrip('.')`-style) normalization: that would silently accept
 * a malformed tenant and diverge from the other classifier ports.
 */
export function normalizeTenantHost(siteHost: unknown): string | undefined {
  if (typeof siteHost !== 'string') {
    return undefined;
  }
  const raw = siteHost.trim();
  if (raw === '') {
    return undefined;
  }
  if (tenantAuthorityHasUserinfo(raw)) {
    return undefined;
  }
  const normalized = normalizeHost(extractHost(raw));
  if (!REGISTRABLE_HOST_RE.test(normalized) || isIpAddress(normalized)) {
    return undefined;
  }
  return normalized;
}

/**
 * Extract the bare authority from a tenant value that may arrive as a full URL
 * (`https://client.com/blog`), a bare domain (`client.com`), or a domain with a
 * trailing path/query/fragment (`client.com/blog?x#y`).
 *
 * Tenant normalization TERMINATES at path, query, fragment (and port, in
 * `normalizeHost`), then validates registrable shape — so `client.com?x`
 * normalizes to `client.com` and stays VALID. The tenant is self-supplied and
 * trusted; rejecting it would silently drop the whole tenant into legacy mode
 * and disable this fix for that tenant. This is the v2 behavior, preserved per
 * the additive directive. (Architect BINDING CORRECTION, 2026-07-13: canonical
 * behavior is ACCEPT; the earlier reject ruling was withdrawn. Fixture rows
 * T13/T14 pin it — do not let a "consistency" edit remove the stripping.)
 *
 * NOTE the asymmetry with the HREF surface, which is intentional: a malformed
 * *authority* in a link is attacker-reachable content and fails closed EXTERNAL;
 * a malformed *tenant* is our own config and is repaired, never rejected.
 */
function extractHost(value: string): string {
  if (ABSOLUTE_HTTP_RE.test(value)) {
    try {
      return new URL(value).hostname;
    } catch {
      // fall through to manual parsing
    }
  }
  return value.split('/')[0].split('?')[0].split('#')[0];
}

/**
 * Normalize a host: lowercase, trim, drop userinfo, strip port, strip ONE
 * trailing `.`, strip a single leading `www.`.
 */
function normalizeHost(host: string): string {
  let h = host.trim().toLowerCase();
  // Drop any userinfo (`user:pass@host`) manual authority extraction may leave in.
  const at = h.lastIndexOf('@');
  if (at !== -1) {
    h = h.slice(at + 1);
  }
  // Strip port (`host:8443`).
  const colon = h.indexOf(':');
  if (colon !== -1) {
    h = h.slice(0, colon);
  }
  // Strip exactly one trailing dot (`client.com.` → `client.com`).
  if (h.endsWith('.')) {
    h = h.slice(0, -1);
  }
  // Strip a single leading `www.`.
  if (h.startsWith('www.')) {
    h = h.slice(4);
  }
  return h;
}

function isIpAddress(host: string): boolean {
  return host.includes(':') || /^\d+(?:\.\d+){3}$/.test(host);
}

function tenantAuthorityHasUserinfo(value: string): boolean {
  if (ABSOLUTE_HTTP_RE.test(value)) {
    try {
      const parsed = new URL(value);
      return parsed.username !== '' || parsed.password !== '';
    } catch {
      return false;
    }
  }
  return value.split('/')[0].split('?')[0].split('#')[0].includes('@');
}
